import React, { useEffect, useState, useRef } from 'react';
import { BellIcon, XIcon, UserIcon, SettingsIcon, LogOutIcon, MenuIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AlgaAssistant from './AlgaAssistant';
import NotificationsMenu from './NotificationsMenu';
interface TopbarProps {
  onMenuClick?: () => void;
}
const Topbar: React.FC<TopbarProps> = ({
  onMenuClick
}) => {
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [searchBarRect, setSearchBarRect] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0
  });
  const [showTypingAnimation, setShowTypingAnimation] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // Animation timers for typing animation
  useEffect(() => {
    const typingInterval = setInterval(() => {
      setShowTypingAnimation(true);
      // Hide typing animation after 2 seconds
      setTimeout(() => {
        setShowTypingAnimation(false);
      }, 2000);
    }, 8000); // Show typing animation every 8 seconds
    return () => {
      clearInterval(typingInterval);
    };
  }, []);
  const handleAlgaClick = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setSearchBarRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      });
    }
    setIsAssistantOpen(true);
  };
  const handleLogout = () => {
    // In a real app, you would clear authentication tokens/cookies here
    navigate('/login');
  };
  // Close notifications and profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <div className="h-16 md:h-20 bg-black md:bg-white sticky top-0 z-30 border-b border-[#171717] md:border-gray-200 flex items-center justify-between px-4 md:px-6" data-id="element-1376">
      <div className="flex items-center" data-id="element-1377">
        {/* Mobile menu button */}
        <button type="button" className="md:hidden -ml-1 mr-2 h-10 w-10 inline-flex items-center justify-center rounded-md text-white hover:text-gray-200 focus:outline-none" onClick={onMenuClick} data-id="element-1378">
          <span className="sr-only" data-id="element-1379">Open sidebar</span>
          <MenuIcon className="h-6 w-6" aria-hidden="true" data-id="element-1380" />
        </button>
        <div className="relative" data-id="element-1381">
          <button ref={buttonRef} onClick={handleAlgaClick} className="flex items-center gap-2 bg-transparent md:bg-white border-0 rounded-full px-3 py-3 md:py-2 hover:bg-[#222222]/10 md:hover:bg-gray-50 transition-colors" data-id="element-1382">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-pink-500 flex-shrink-0" data-id="element-1383">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/x28iigDaSByhSaf2N4iVm6/IMG_0007.png" alt="ALGA AI Assistant" className="w-full h-full object-cover" data-id="element-1384" />
            </div>
            {showTypingAnimation ? <div className="flex space-x-1 ml-1" data-id="element-1385">
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" data-id="element-1386"></div>
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
              animationDelay: '0.2s'
            }} data-id="element-1387"></div>
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
              animationDelay: '0.4s'
            }} data-id="element-1388"></div>
              </div> : <span className="text-sm font-medium text-gray-300 md:text-gray-600 ml-1 inline" data-id="element-1389">
                Ask ALGA
              </span>}
          </button>
        </div>
      </div>
      <div className="flex items-center space-x-4" data-id="element-1390">
        <div className="relative" ref={notificationsRef} data-id="element-1391">
          <button onClick={() => setIsNotificationsOpen(!isNotificationsOpen)} className="relative hover:bg-[#222222] md:hover:bg-gray-100 p-2 rounded-full transition-colors" data-id="element-1392">
            <BellIcon className="text-white md:text-gray-600 cursor-pointer hover:text-custom-green" size={20} data-id="element-1393" />
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center" data-id="element-1394">
              3
            </span>
          </button>
          <NotificationsMenu isOpen={isNotificationsOpen} onClose={() => setIsNotificationsOpen(false)} data-id="element-1395" />
        </div>
        <div className="relative" ref={profileMenuRef} data-id="element-1396">
          <button onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)} className="flex items-center space-x-3 hover:bg-[#222222] md:hover:bg-gray-50 rounded-full transition-colors" data-id="element-1397">
            <div className="h-9 w-9 rounded-full overflow-hidden border-2 border-custom-green" data-id="element-1398">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4hFhikAPJ46vhrcKePNA5J/IMG_0014.jpg" alt="Profilo" className="h-full w-full object-cover" data-id="element-1399" />
            </div>
            <div className="hidden md:block text-left" data-id="element-1400">
              <div className="text-sm font-medium text-gray-900" data-id="element-1401">
                Edoardo Gatner
              </div>
              <div className="text-xs text-gray-500" data-id="element-1402">CEO</div>
            </div>
          </button>
          {/* Profile Dropdown Menu */}
          {isProfileMenuOpen && <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50 border border-gray-200" data-id="element-1403">
              <div className="px-4 py-3 border-b border-gray-100" data-id="element-1404">
                <p className="text-sm font-medium text-gray-900" data-id="element-1405">
                  Edoardo Gatner
                </p>
                <p className="text-xs text-gray-500 truncate" data-id="element-1406">
                  edoardo.gatner@example.com
                </p>
              </div>
              <ul data-id="element-1407">
                <li data-id="element-1408">
                  <button onClick={() => {
                setIsProfileMenuOpen(false);
                navigate('/public-profile');
              }} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left" data-id="element-1409">
                    <UserIcon size={16} className="mr-2" data-id="element-1410" />
                    Il mio profilo
                  </button>
                </li>
                <li data-id="element-1411">
                  <button onClick={() => setIsProfileMenuOpen(false)} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left" data-id="element-1412">
                    <SettingsIcon size={16} className="mr-2" data-id="element-1413" />
                    Impostazioni
                  </button>
                </li>
                <li className="border-t border-gray-100" data-id="element-1414">
                  <button onClick={handleLogout} className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left" data-id="element-1415">
                    <LogOutIcon size={16} className="mr-2" data-id="element-1416" />
                    Logout
                  </button>
                </li>
              </ul>
            </div>}
        </div>
      </div>
      {/* ALGA Assistant Modal */}
      <AlgaAssistant isOpen={isAssistantOpen} onClose={() => setIsAssistantOpen(false)} searchBarRect={searchBarRect} data-id="element-1417" />
    </div>;
};
export default Topbar;